/**
 * PatientHomePage — the patient's home hub (/dashboard).
 *
 * A calm, centered landing in the style of the AI apps: a greeting, CareStart as
 * the larger choice, and their records as a compact set below. CareMap joins on
 * development and sandbox servers (same gate as AppSwitcher). Everything is the
 * shared SelectableChoiceCard so it inherits the theme's outlined-surface +
 * hover/focus treatment.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import RecordVoiceOverRounded from "@mui/icons-material/RecordVoiceOverRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import MedicationRounded from "@mui/icons-material/MedicationRounded";
import AssignmentRounded from "@mui/icons-material/AssignmentRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import MapRounded from "@mui/icons-material/MapRounded";

import { useAuth } from "hooks/useAuth";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import { masterChartPath } from "constants/masterChartRoutes";
import { BrandGlowLayer } from "components/common/BrandGlow";
import { isDevEnvironment } from "utils/isDevEnvironment";

// Hidden for now, each for its own reason:
// - VisitStart: not finished. Patients still reach it from a clinic's QR code.
// - Messages: facility <> patient comms aren't live; its route is commented out
//   in PatientRoutes.jsx, so the tile led nowhere.
const ASK_TILES = [
  {
    icon: <RecordVoiceOverRounded />,
    title: "CareStart",
    description: "Check your symptoms",
    path: "/dashboard/carestart",
  },
  {
    icon: <TranslateRounded />,
    title: "CareStart 3",
    description: "Check your symptoms in your language",
    path: "/dashboard/carestart3",
  },
];

const RECORD_TILES = [
  {
    icon: <HealthAndSafetyRounded />,
    title: "MasterChart",
    description: "View and update your health information",
    path: masterChartPath(),
  },
  {
    icon: <MedicationRounded />,
    title: "My Medications",
    description: "Keep your medication list current",
    path: "/dashboard/medications",
  },
  {
    icon: <AssignmentRounded />,
    title: "Discharge Instructions",
    description: "Summaries from your facility visits",
    path: "/dashboard/discharge-instructions",
  },
  {
    icon: <QrCode2Rounded />,
    title: "Share with provider",
    description: "Give a clinician secure access",
    path: masterChartPath("share"),
  },
];

const CAREMAP_TILE = {
  icon: <MapRounded />,
  title: "CareMap",
  description: "Find care near you",
  path: "/caremap",
};

const PatientHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Sign-up never asks for a name, so the greeting can't use one. First visit is the
  // sign-in that created the account.
  const firstVisit =
    user?.metadata?.creationTime &&
    user.metadata.creationTime === user.metadata.lastSignInTime;
  const askTiles = isDevEnvironment() ? [...ASK_TILES, CAREMAP_TILE] : ASK_TILES;

  return (
    <Box
      sx={{
        // Not positioned, so BrandGlowLayer fills the whole <main>, not this column;
        // isolation keeps it behind the cards but above the page.
        isolation: "isolate",
        maxWidth: 760,
        mx: "auto",
        // Centered in the viewport like the AI apps' start screens; the offsets are
        // the dashboard page column's padding and, on phones, the top app bar.
        minHeight: "calc(100dvh - 128px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        py: 3,
      }}
    >
      <BrandGlowLayer preset="home" />
      {/* Same size as the other page titles (DashboardPageHeader). */}
      <Typography
        variant="h4"
        component="h1"
        textAlign="center"
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        {firstVisit ? "Welcome" : "Welcome back"}
      </Typography>
      <Typography
        color="text.secondary"
        textAlign="center"
        sx={{ mt: 1, mb: { xs: 3, md: 4 } }}
      >
        What would you like to do today?
      </Typography>

      <Grid container spacing={2}>
        {askTiles.map((tile) => (
          <Grid item xs={12} sm={6} key={tile.path}>
            <SelectableChoiceCard
              icon={tile.icon}
              title={tile.title}
              description={tile.description}
              onClick={() => navigate(tile.path)}
              alignIcon="center"
              sx={{ py: 2.5 }}
            />
          </Grid>
        ))}
      </Grid>

      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mt: { xs: 3, md: 4 }, mb: 1 }}
      >
        Your records
      </Typography>
      <Grid container spacing={1.5}>
        {RECORD_TILES.map((tile) => (
          <Grid item xs={12} sm={6} key={tile.path}>
            <SelectableChoiceCard
              icon={tile.icon}
              title={tile.title}
              description={tile.description}
              onClick={() => navigate(tile.path)}
              alignIcon="center"
              sx={{ py: 1.5 }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PatientHomePage;
